import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";

import { api_without_cancellation } from "../api/api";
import ScoreboardIcon from "../assets/checklist/scoreboard-icon-tab.svg";
import UserChatBubbleScoreboard from "../Components/Forms/UserChatBtnScoreboard";
import CircularGaugeScoreBoard from "../Components/CheckLists/new-checklist/GaugeScoreBoard";

// Layout strategy: the content is laid out at a virtual width chosen from a
// small set of candidates — one per possible column count — and then shrunk
// to fit the viewport with a transform. For each candidate column count C
// the virtual width is exactly wide enough for C cards per row; the
// candidate that yields the LARGEST on-screen scale wins. Because the scale
// is limited by BOTH availW/virtualW and availH/naturalH, over-widening
// (one giant row) and over-stacking (one skinny column) both score badly,
// so the winner is a balanced grid rather than a single line.
//
// Resize handling is two-tier:
//   1. Cheap pass (every observer tick): keep the settled column layout and
//      just rescale it to the new viewport with direct style mutations —
//      no probing, no setState, no React re-render. Dragging the window
//      edge is therefore a single smooth uniform scale, zero flicker.
//   2. Full solve (debounced ~200ms after the last tick): re-run the
//      column-count probe and commit the winner, syncing React state once.
const uiScale = Math.min(window.innerWidth / 3840, window.innerHeight / 2160);
// TV sizing knobs — tuned for a 55" panel viewed from ~20 feet
const TITLE_BAR_HEIGHT = 72;
const GAUGE_SIZE = 800 * uiScale;
const NAME_FONT_SIZE = 200 * uiScale;
const CASES_FONT_SIZE = 200 * uiScale;
const CARD_MIN_WIDTH = 2500 * uiScale;
const AVATAR_SIZE = 70 * uiScale;
const RANK_BADGE_SIZE = 200 * uiScale;
const SUBTITLE_FONT_SIZE = 100 * uiScale;

const GRID_GAP = 20;
const CONTENT_PADDING_X = 10; // must match the content div's horizontal padding
const RESIZE_SETTLE_MS = 200; // full solve runs this long after the last resize tick

const RANK_STYLES = {
  0: { background: "#f5c518", color: "#5b4300", glow: "rgba(245,197,24,0.45)" }, // gold
  1: { background: "#c0c4cc", color: "#3d3f42", glow: "rgba(192,196,204,0.35)" }, // silver
  2: { background: "#cd8a4b", color: "#4a2c0f", glow: "rgba(205,138,75,0.35)" }, // bronze
};

// ---------- BigGauge ----------
// Guarantees CircularGaugeScoreBoard renders at exactly GAUGE_SIZE on screen.
// The component sets width/height attributes from its `size` prop, but the
// legacy .circle-chart CSS class can override them; this wrapper measures the
// actual rendered size and force-scales it with a transform. If the size prop
// wins, the factor resolves to ~1 and this is a no-op. Transforms don't
// affect offsetWidth/offsetHeight, so the observer cannot loop.
const BigGauge = ({ percentage, targetSize = GAUGE_SIZE }) => {
  const innerRef = useRef(null);
  const [factor, setFactor] = useState(1);

  useLayoutEffect(() => {
    const inner = innerRef.current;
    if (!inner) return undefined;

    const measure = () => {
      const natural = Math.max(inner.offsetWidth, inner.offsetHeight);
      if (natural > 0) {
        const next = targetSize / natural;
        setFactor((prev) => (Math.abs(prev - next) > 0.01 ? next : prev));
      }
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(inner);
    return () => observer.disconnect();
  }, [targetSize]);

  return (
    <div
      style={{
        width: targetSize,
        height: targetSize,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "visible",
      }}
    >
      <div
        ref={innerRef}
        style={{
          display: "inline-flex",
          transform: `scale(${factor})`,
          transformOrigin: "center center",
        }}
      >
        <CircularGaugeScoreBoard percentage={percentage} size={targetSize} />
      </div>
    </div>
  );
};

// ---------- RankBadge ----------
const RankBadge = ({ index, size = RANK_BADGE_SIZE }) => {
  const medal = RANK_STYLES[index];
  return (
    <span
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: medal ? medal.background : "#eef1f5",
        color: medal ? medal.color : "#7a828f",
        fontWeight: 800,
        fontSize: size * 0.44,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        boxShadow: medal ? `0 0 20px ${medal.glow}` : "none",
      }}
    >
      {index + 1}
    </span>
  );
};

// ---------- Score Card ----------
// Structure: name row on top, gauge in the middle, footer with avatars on
// the left and the case count pinned to the bottom-right corner.
const ScoreCard = ({ rank, title, subtitle, caseCount, gauge, avatars }) => {
  const medal = RANK_STYLES[rank];
  return (
    <div
      style={{
        flex: `1 1 var(--card-w, ${CARD_MIN_WIDTH}px)`,
        maxWidth: "100%",
        minWidth: 0,
        background: "#ffffff",
        borderRadius: "24px",
        padding: "28px 32px 24px",
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        borderTop: `8px solid ${medal ? medal.background : "#dfe4ea"}`,
      }}
    >
      {/* ---- Name row ---- */}
      <div style={{ display: "flex", alignItems: "center", gap: "20px", minWidth: 0 }}>
        <RankBadge index={rank} />
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: `${NAME_FONT_SIZE}px`,
              fontWeight: 800,
              color: "#1a1a2e",
              lineHeight: 1.1,
              letterSpacing: "-0.01em",
              overflowWrap: "break-word", // full name always shown; wraps to next line
            }}
          >
            {title}
          </div>
          {subtitle && (
            <div
              style={{
                fontSize: `${SUBTITLE_FONT_SIZE}px`,
                color: "#7a828f",
                marginTop: "10px",
              }}
            >
              {subtitle}
            </div>
          )}
        </div>
      </div>

      {/* ---- Giant gauge ---- */}
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
        {gauge}
      </div>

      {/* ---- Footer: avatars left, cases pinned bottom-right ---- */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          gap: "16px",
          marginTop: "auto",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: "10px",
            alignItems: "center",
            flexWrap: "wrap",
            minWidth: 0,
          }}
        >
          {avatars}
        </div>

        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div
            style={{
              fontSize: `${CASES_FONT_SIZE}px`,
              fontWeight: 800,
              color: "#1a1a2e",
              lineHeight: 1,
            }}
          >
            {caseCount}
          </div>
          <div
            style={{
              fontSize: "18px",
              fontWeight: 700,
              color: "#7a828f",
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              marginTop: "4px",
            }}
          >
            Cases
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------- Section header ----------
const SectionHeader = ({ children, first }) => (
  <h2
    style={{
      fontSize: "32px",
      fontWeight: 800,
      color: "#f5c518",
      letterSpacing: "0.25em",
      textTransform: "uppercase",
      margin: first ? "0 0 28px" : "56px 0 28px",
      display: "flex",
      alignItems: "center",
      gap: "20px",
    }}
  >
    {children}
    <span style={{ flex: 1, height: "2px", background: "rgba(245,197,24,0.25)" }} />
  </h2>
);

// ---------- Card row (wrapping flexbox) ----------
const CardGrid = ({ children, cardWidth }) => (
  <div
    style={{
      display: "flex",
      flexWrap: "wrap",
      gap: `${GRID_GAP}px`,
      alignItems: "stretch",
      flex: "1",
      ...(cardWidth ? { "--card-w": `${cardWidth}px` } : {}),
    }}
  >
    {children}
  </div>
);

// ---------- Main Component ----------
const ScoreBoard = () => {
  const [searchParams] = useSearchParams();
  const include = searchParams.get("include") || "both";

  const [data, setData] = useState({ firm_users: [], teams: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const sizerRef = useRef(null);
  // width: the virtual layout width of the content (null = 100% of container)
  const [metrics, setMetrics] = useState({
    scale: 1,
    height: 0,
    width: null,
    cardWidth: null,
    cols: null,
  });
  const userOnlineStatus = useSelector((state) => state.chat2.userOnlineStatus);

  // ---------- Data Fetching ----------
  useEffect(() => {
    let cancelled = false;

    const fetchData = (isInitial) => {
      if (isInitial) {
        setLoading(true);
        setError(null);
      }

      api_without_cancellation
        .get("/api/homepage/scoreboard/", { params: { include } })
        .then((res) => {
          if (!cancelled) {
            setData({ firm_users: res.data.firm_users || [], teams: res.data.teams || [] });
            setError(null);
            setLastUpdated(new Date().toLocaleTimeString());
          }
        })
        .catch(() => {
          if (!cancelled && isInitial) setError("Failed to load scoreboard data.");
        })
        .finally(() => {
          if (!cancelled && isInitial) setLoading(false);
        });
    };

    fetchData(true);
    const interval = setInterval(() => fetchData(false), 60000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [include]);

  // ---------- Fill-the-screen layout solver ----------
  // fullSolve: for each candidate column count C (1..most cards in any
  // section), the virtual content width is the exact width that fits C
  // cards per row. The candidate is scored by its on-screen scale
  //     s(C) = min(1, availW / virtualW(C), availH / naturalH(C))
  // and the best C wins. Both extremes lose: one huge row makes virtualW
  // enormous (availW/virtualW tanks), one column makes naturalH enormous
  // (availH/naturalH tanks) — so the result is a balanced multi-row grid.
  //
  // naturalH(C) is measured by imperatively setting style.width and reading
  // scrollHeight, all before paint, so the probing is invisible.
  //
  // fullSolve is expensive (multiple forced layouts) and its setMetrics
  // triggers a React re-render, so it must NOT run on every resize tick —
  // that's exactly the flicker. Instead, every tick runs cheapRescale
  // (pure style mutations, keeps the settled layout, just refits the
  // scale), and fullSolve is debounced until RESIZE_SETTLE_MS of quiet.
  useLayoutEffect(() => {
    const container = containerRef.current;
    const content = contentRef.current;
    if (!container || !content) return undefined;

    const maxCards = Math.max(
      include === "both" || include === "teams" ? data.teams.length : 0,
      include === "both" || include === "firm_users" ? data.firm_users.length : 0,
      1
    );

    let frame = null;
    let settleTimer = null;
    const lastRef = { availW: 0, availH: 0, width: 0, natH: 0 };

    const widthForCols = (c) => CONTENT_PADDING_X * 2 + c * CARD_MIN_WIDTH + (c - 1) * GRID_GAP;

    const fullSolve = () => {
      const availW = container.clientWidth;
      const availH = container.clientHeight;
      if (!availW || !availH) return;

      const curW = content.offsetWidth; // transform-independent
      const curH = content.scrollHeight; // transform-independent
      if (!curW || !curH) return;

      // Echo of our own final mutation (or nothing changed) -> settled.
      if (
        availW === lastRef.availW &&
        availH === lastRef.availH &&
        Math.abs(curW - lastRef.width) < 1 &&
        Math.abs(curH - lastRef.natH) < 1
      ) {
        return;
      }

      // Probe every column count synchronously (pre-paint, invisible).
      let best = null;
      let lastProbedW = -1;
      let bestCols = 1;
      for (let c = 1; c <= maxCards; c++) {
        const w = Math.max(availW, widthForCols(c));
        if (w === lastProbedW) continue;
        lastProbedW = w;

        content.style.width = `${w}px`;
        const h = content.scrollHeight;
        const s = Math.min(1, availW / w, availH / h);

        if (!best || s > best.s + 0.0005) {
          best = { w, h, s };
          bestCols = c;
        }
      }
      if (!best) return;

      // If height-bound (s came from availH/h, not availW/w), the virtual
      // width best.w is under-using available width once scaled. Re-derive
      // the width so that w * s === availW exactly, then re-measure height
      // at that width (card aspect changes slightly, height barely moves,
      // but we recompute for correctness rather than assuming).
      const targetW = availW / best.s;
      if (targetW > best.w + 1) {
        content.style.width = `${targetW}px`;
        const h2 = content.scrollHeight;
        const s2 = Math.min(1, availH / h2); // width term is now == 1 by construction
        best = { w: targetW, h: h2, s: s2 };
      }

      const cardWidth = (best.w - CONTENT_PADDING_X * 2 - (bestCols - 1) * GRID_GAP) / bestCols;

      // Commit the winner imperatively (width AND transform AND sizer
      // height) so this paint is already correct even if setMetrics below
      // bails or React batches; state is then synced to the same values.
      content.style.width = `${best.w}px`;
      content.style.transform = `scale(${best.s})`;
      const height = best.h * best.s;
      if (sizerRef.current) sizerRef.current.style.height = `${height}px`;

      lastRef.availW = availW;
      lastRef.availH = availH;
      lastRef.width = best.w;
      lastRef.natH = best.h;

      setMetrics((prev) => {
        if (
          Math.abs(prev.scale - best.s) < 0.005 &&
          Math.abs(prev.height - height) < 1 &&
          Math.abs((prev.width ?? 0) - best.w) < 1 &&
          Math.abs((prev.cardWidth ?? 0) - cardWidth) < 1
        ) {
          return prev;
        }
        return { scale: best.s, height, width: best.w, cardWidth, cols: bestCols };
      });
    };

    // Cheap per-tick pass: keep the settled virtual layout, just refit its
    // scale to the live viewport. Two style writes, zero probes, zero
    // re-renders — resizing reads as one continuous smooth zoom.
    const cheapRescale = () => {
      frame = null;
      if (!lastRef.width) {
        // Nothing settled yet (first run / data just changed) — solve now.
        fullSolve();
        return;
      }
      const availW = container.clientWidth;
      const availH = container.clientHeight;
      if (!availW || !availH) return;

      const s = Math.min(1, availW / lastRef.width, availH / lastRef.natH);
      content.style.transform = `scale(${s})`;
      if (sizerRef.current) sizerRef.current.style.height = `${lastRef.natH * s}px`;
    };

    const schedule = () => {
      if (frame === null) frame = requestAnimationFrame(cheapRescale);
      if (settleTimer !== null) clearTimeout(settleTimer);
      settleTimer = setTimeout(() => {
        settleTimer = null;
        fullSolve();
      }, RESIZE_SETTLE_MS);
    };

    schedule();
    const observer = new ResizeObserver(schedule);
    observer.observe(container);
    observer.observe(content);
    return () => {
      observer.disconnect();
      if (frame !== null) cancelAnimationFrame(frame);
      if (settleTimer !== null) clearTimeout(settleTimer);
    };
  }, [loading, error, data, include]);

  // ---------- Helpers ----------
  const showTeams = include === "both" || include === "teams";
  const showUsers = include === "both" || include === "firm_users";

  const sortedTeams = [...data.teams].sort(
    (a, b) => b.completion_percentage - a.completion_percentage
  );
  const sortedUsers = [...data.firm_users].sort(
    (a, b) => b.completion_percentage - a.completion_percentage
  );

  // ---------- Render ----------
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        display: "flex",
        flexDirection: "column",
        background: "radial-gradient(ellipse at top, #16213e 0%, #0f1626 55%, #0a0f1c 100%)",
        overflow: "hidden",
      }}
    >
      {/* ===== Header ===== */}
      <div
        style={{
          height: `${TITLE_BAR_HEIGHT}px`,
          flex: "0 0 auto",
          background: "rgba(0,0,0,0.4)",
          color: "white",
          display: "flex",
          alignItems: "center",
          padding: "0 40px",
          borderBottom: "4px solid #f5c518",
          gap: "16px",
        }}
      >
        <img
          src={ScoreboardIcon}
          alt=""
          style={{ height: "38px", filter: "brightness(0) invert(1)" }}
        />
        <span style={{ fontSize: "34px", fontWeight: 800, letterSpacing: "0.3em" }}>
          SCOREBOARD
        </span>
        <span style={{ marginLeft: "auto", fontSize: "22px", color: "#8b96ad" }}>
          Updated: {lastUpdated || "--:--:--"}
        </span>
        <span
          style={{
            display: "inline-block",
            width: "14px",
            height: "14px",
            borderRadius: "50%",
            background: "#2ecc71",
            boxShadow: "0 0 16px rgba(46,204,113,0.8)",
            animation: "pulse 1.5s infinite",
          }}
        />
      </div>

      {/* ===== Scrollable viewport ===== */}
      <div
        ref={containerRef}
        className="invisible-scrollbar"
        style={{
          flex: 1,
          overflowY: "hidden",
          overflowX: "hidden",
          scrollbarGutter: "stable",
        }}
      >
        {loading && (
          <div
            className="d-flex align-items-center justify-content-center"
            style={{ height: "240px", fontSize: "28px", color: "#f2f5fa" }}
          >
            <span className="spinner-border spinner-border-sm m-r-5" />
            Loading Scoreboard...
          </div>
        )}

        {!loading && error && (
          <div
            className="d-flex align-items-center justify-content-center"
            style={{ height: "240px", color: "#e74c3c", fontSize: "28px" }}
          >
            {error}
          </div>
        )}

        {!loading && !error && (
          /* Sizer: reserves the visual (scaled) height so scrolling matches
             what's on screen. Not observed by the ResizeObserver; its height
             is also mutated imperatively during cheap rescales. */
          <div
            ref={sizerRef}
            style={{
              height: metrics.height ? `${metrics.height}px` : "auto",
              position: "relative",
              overflow: "hidden",
            }}
          >
            {/* Content laid out at the virtual width; shrink applied as a
                pure transform. Scaled width === viewport width, so origin
                top-left keeps it flush with both edges. */}
            <div
              ref={contentRef}
              style={{
                width: metrics.width ? `${metrics.width}px` : "100%",
                boxSizing: "border-box",
                padding: `28px ${CONTENT_PADDING_X}px`,
                transform: `scale(${metrics.scale})`,
                transformOrigin: "top left",
              }}
            >
              {/* ---- Teams Section ---- */}
              {showTeams && (
                <>
                  <SectionHeader first>Teams</SectionHeader>
                  <CardGrid cardWidth={metrics.cardWidth}>
                    {sortedTeams.map((team, idx) => {
                      const members = team.firm_users || [];
                      const avatars = members
                        .slice(0, 5)
                        .map((fu) => (
                          <UserChatBubbleScoreboard
                            key={fu.id}
                            first_name={fu?.user?.first_name}
                            last_name={fu?.user?.last_name}
                            profilePic={fu?.profile_pic_19p ?? ""}
                            isActive={userOnlineStatus?.[fu?.user?.id]?.isOnline ?? false}
                            hideIcon
                            size={AVATAR_SIZE}
                          />
                        ));
                      if (members.length > 5) {
                        avatars.push(
                          <span
                            key="extra"
                            style={{ fontSize: "22px", fontWeight: 700, color: "#7a828f" }}
                          >
                            +{members.length - 5}
                          </span>
                        );
                      }
                      return (
                        <ScoreCard
                          key={team.id}
                          rank={idx}
                          title={team.team_name}
                          subtitle={`${members.length} members`}
                          caseCount={team.case_count}
                          avatars={[]}
                          gauge={<BigGauge percentage={team.completion_percentage} />}
                        />
                      );
                    })}
                  </CardGrid>
                  {sortedTeams.length === 0 && (
                    <div style={{ fontSize: "24px", color: "#8b96ad", textAlign: "center" }}>
                      No teams found.
                    </div>
                  )}
                </>
              )}

              {/* ---- Users Section ---- */}
              {showUsers && (
                <>
                  <SectionHeader first={!showTeams}>Firm Users</SectionHeader>
                  <CardGrid cardWidth={metrics.cardWidth}>
                    {sortedUsers.map((fu, idx) => (
                      <ScoreCard
                        key={fu.id}
                        rank={idx}
                        title={`${fu?.user?.first_name || ""} ${fu?.user?.last_name || ""}`.trim()}
                        subtitle={fu?.user?.email || ""}
                        caseCount={fu.case_count}
                        avatars={[]}
                        gauge={<BigGauge percentage={fu.completion_percentage} />}
                      />
                    ))}
                  </CardGrid>
                  {sortedUsers.length === 0 && (
                    <div style={{ fontSize: "24px", color: "#8b96ad", textAlign: "center" }}>
                      No firm users found.
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes pulse {
          0% { opacity: 0.4; transform: scale(0.95); }
          50% { opacity: 1; transform: scale(1.05); }
          100% { opacity: 0.4; transform: scale(0.95); }
        }
      `}</style>
    </div>
  );
};

export default ScoreBoard;
